import React, { useState, useEffect, useCallback } from 'react';
import { startOfMonth, addMonths, subMonths, format } from 'date-fns';
import { Employee } from '@/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MonthPicker } from '@/components/ui/month-picker';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Mail,
  Phone,
  Edit,
  Calendar,
  Clock,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertCircle,
  Briefcase,
  FileText,
  Activity,
  DollarSign,
  UserCheck,
  Timer,
  CalendarDays,
  ArrowLeft,
  ArrowRight,
  X,
  Check,
  Loader2,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import { getContractStatusConfig, formatContractStatus } from '@/lib/employees/contract-status';
import { useEmployeePlanFulfillment } from '@/hooks/employees/usePlanFulfillment';
import { useRoles } from '@/hooks/employees/useRoles';
import { useEmploymentTypes } from '@/hooks/compliance/useEmploymentTypes';
import { cn } from '@/lib/core/utils';
import EmployeeShiftCalendarView from './EmployeeShiftCalendarView';

interface EmployeeDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee: Employee | null;
  businessId: string | null;
  onEmployeeUpdated?: (employee: Employee) => void;
  defaultTab?: 'overview' | 'time' | 'payroll' | 'documents' | 'activity';
}

interface EmployeeActivity {
  id: string;
  type: 'shift' | 'clock_in' | 'clock_out' | 'status_change' | 'contract';
  created_at: string;
  data: any;
}

const statusConfig = {
  ahead: {
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    icon: TrendingUp,
    label: 'Ahead of plan',
  },
  on_track: {
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
    icon: Minus,
    label: 'On track',
  },
  behind: {
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30',
    icon: TrendingDown,
    label: 'Behind plan',
  },
  significantly_behind: {
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    icon: AlertCircle,
    label: 'Significantly behind',
  },
  no_target: {
    color: 'text-muted-foreground',
    bgColor: 'bg-muted',
    icon: Minus,
    label: 'No target set',
  },
};

const EmployeeDetailModal: React.FC<EmployeeDetailModalProps> = ({
  open,
  onOpenChange,
  employee,
  businessId,
  onEmployeeUpdated,
  defaultTab = 'overview',
}) => {
  const [activities, setActivities] = useState<EmployeeActivity[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [calendarShifts, setCalendarShifts] = useState<any[]>([]);
  const [loadingShifts, setLoadingShifts] = useState(true);
  const [recentTimeLogs, setRecentTimeLogs] = useState<any[]>([]);

  // Month picker state for controlling all stats
  const [selectedMonth, setSelectedMonth] = useState(() => startOfMonth(new Date()));

  // Inline editing state
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRoleId, setEditRoleId] = useState<string | null>(null);
  const [editEmploymentTypeId, setEditEmploymentTypeId] = useState<string | null>(null);
  const [editSalaryAdjustment, setEditSalaryAdjustment] = useState<number | null>(null);

  // Fetch roles and employment types for editing
  const { data: roles } = useRoles(businessId || '');
  const { employmentTypes, isLoading: loadingTypes } = useEmploymentTypes(businessId || '');

  // Fetch plan fulfillment data for the selected month
  const { data: fulfillment, isLoading: fulfillmentLoading } = useEmployeePlanFulfillment(
    businessId,
    employee?.id ?? null,
    selectedMonth
  );

  // Month navigation handlers
  const handlePrevMonth = useCallback(() => {
    setSelectedMonth(prev => subMonths(prev, 1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setSelectedMonth(prev => addMonths(prev, 1));
  }, []);

  const handleMonthSelect = useCallback((date: Date) => {
    setSelectedMonth(startOfMonth(date));
  }, []);

  // Initialize edit form when entering edit mode
  const startEditing = () => {
    if (employee) {
      setEditName(employee.name);
      setEditEmail(employee.email || '');
      setEditRoleId(employee.role_id || null);
      setEditEmploymentTypeId(employee.employment_type_id || null);
      setEditSalaryAdjustment(employee.salary_adjustment || null);
      setIsEditing(true);
    }
  };

  const cancelEditing = () => {
    setIsEditing(false);
  };

  const saveChanges = async () => {
    if (!employee) return;

    if (!editName.trim() || !editEmail.trim()) {
      toast.error('Name and Email are required.');
      return;
    }

    setIsSaving(true);

    try {
      const { data, error } = await supabase
        .from('employees')
        .update({
          name: editName,
          email: editEmail,
          role_id: editRoleId,
          employment_type_id: editEmploymentTypeId,
          salary_adjustment: editSalaryAdjustment
        })
        .eq('id', employee.id)
        .select()
        .single();

      if (error) throw error;

      if (data) {
        toast.success('Employee updated successfully!');
        onEmployeeUpdated?.(data as Employee);
        setIsEditing(false);
      }
    } catch (error: unknown) {
      console.error('Error updating employee:', error);
      toast.error('Failed to update employee', {
        description: error instanceof Error ? error.message : String(error) || 'An unexpected error occurred.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (employee?.id && open) {
      fetchActivities();
      fetchCalendarShifts();
      fetchRecentTimeLogs();
    }
    // Reset editing state when modal closes
    if (!open) {
      setIsEditing(false);
    }
  }, [employee?.id, open, selectedMonth]);

  const fetchActivities = async () => {
    if (!employee) return;
    try {
      setLoadingActivities(true);
      const allActivities: EmployeeActivity[] = [];
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      // Fetch recent shifts
      const { data: shifts, error: shiftsError } = await supabase
        .from('shifts')
        .select('id, start_time, end_time, status, notes, created_at')
        .eq('employee_id', employee.id)
        .gte('start_time', thirtyDaysAgo.toISOString())
        .order('start_time', { ascending: false })
        .limit(20);

      if (!shiftsError && shifts) {
        shifts.forEach((shift) => {
          allActivities.push({
            id: `shift-${shift.id}`,
            type: 'shift',
            created_at: shift.start_time,
            data: shift,
          });
        });
      }

      // Fetch recent time logs
      const { data: timeLogs, error: timeLogsError } = await supabase
        .from('time_logs')
        .select('id, event_type, scanned_at, location_name')
        .eq('employee_id', employee.id)
        .gte('scanned_at', thirtyDaysAgo.toISOString())
        .order('scanned_at', { ascending: false })
        .limit(50);

      if (!timeLogsError && timeLogs) {
        timeLogs.forEach((log) => {
          allActivities.push({
            id: `timelog-${log.id}`,
            type: log.event_type === 'arrival' ? 'clock_in' : 'clock_out',
            created_at: log.scanned_at,
            data: log,
          });
        });
      }

      // Sort all activities by date
      allActivities.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setActivities(allActivities.slice(0, 30)); // Limit to 30 most recent
    } catch (error: any) {
      console.error('Error fetching activities:', error);
      toast.error('Failed to load activities');
    } finally {
      setLoadingActivities(false);
    }
  };

  const fetchCalendarShifts = async () => {
    if (!employee) return;
    try {
      setLoadingShifts(true);
      // Fetch shifts for the selected month
      const startDate = startOfMonth(selectedMonth);
      const endDate = addMonths(startDate, 1);

      const { data, error } = await supabase
        .from('shifts')
        .select('id, start_time, end_time, status, notes')
        .eq('employee_id', employee.id)
        .gte('start_time', startDate.toISOString())
        .lt('start_time', endDate.toISOString())
        .order('start_time', { ascending: true });

      if (!error && data) {
        setCalendarShifts(data);
      }
    } catch (error) {
      console.error('Error fetching calendar shifts:', error);
    } finally {
      setLoadingShifts(false);
    }
  };

  const fetchRecentTimeLogs = async () => {
    if (!employee) return;
    try {
      const { data, error } = await supabase
        .from('time_logs')
        .select('id, event_type, scanned_at, location_name')
        .eq('employee_id', employee.id)
        .order('scanned_at', { ascending: false })
        .limit(10);

      if (!error && data) {
        setRecentTimeLogs(data);
      }
    } catch (error) {
      console.error('Error fetching time logs:', error);
    }
  };

  const getInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const getAvatarColor = (name: string): string => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-orange-500',
      'bg-pink-500',
      'bg-cyan-500',
      'bg-indigo-500',
      'bg-teal-500',
      'bg-rose-500',
      'bg-amber-500',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const getActivityIcon = (type: EmployeeActivity['type']) => {
    switch (type) {
      case 'shift':
        return <Calendar className="h-4 w-4 text-primary" />;
      case 'clock_in':
        return <UserCheck className="h-4 w-4 text-green-500" />;
      case 'clock_out':
        return <Timer className="h-4 w-4 text-orange-500" />;
      case 'status_change':
        return <Activity className="h-4 w-4 text-blue-500" />;
      case 'contract':
        return <FileText className="h-4 w-4 text-purple-500" />;
      default:
        return <Calendar className="h-4 w-4" />;
    }
  };

  const renderActivityContent = (activity: EmployeeActivity) => {
    if (activity.type === 'shift') {
      const start = new Date(activity.data.start_time);
      const end = new Date(activity.data.end_time);
      const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);

      return (
        <div className="space-y-1">
          <div className="font-medium">Shift Scheduled</div>
          <div className="text-sm text-muted-foreground">
            {start.toLocaleDateString('en-GB')} {start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} -{' '}
            {end.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} ({hours.toFixed(1)}h)
          </div>
          {activity.data.status && (
            <Badge variant="outline" className="text-xs">
              {activity.data.status}
            </Badge>
          )}
        </div>
      );
    } else if (activity.type === 'clock_in') {
      return (
        <div className="space-y-1">
          <div className="font-medium">Clocked In</div>
          <div className="text-sm text-muted-foreground">
            {activity.data.location_name || 'Main Location'}
          </div>
        </div>
      );
    } else if (activity.type === 'clock_out') {
      return (
        <div className="space-y-1">
          <div className="font-medium">Clocked Out</div>
          <div className="text-sm text-muted-foreground">
            {activity.data.location_name || 'Main Location'}
          </div>
        </div>
      );
    }
    return null;
  };

  // Early return if no employee
  if (!employee) return null;

  const avatarColor = getAvatarColor(employee.name);
  const config = fulfillment ? statusConfig[fulfillment.status] : statusConfig.no_target;
  const StatusIcon = config.icon;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col p-0">
          {/* Fixed Header with Inline Editing */}
          <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4 border-b">
            <div className="flex items-start gap-4">
              <Avatar className={cn('h-14 w-14 text-white text-xl', avatarColor)}>
                <AvatarFallback className={cn('text-xl font-medium', avatarColor)}>
                  {getInitials(isEditing ? editName : employee.name)}
                </AvatarFallback>
              </Avatar>

              {isEditing ? (
                /* Edit Mode */
                <div className="flex-1 min-w-0 space-y-3">
                  <div className="flex items-center gap-2">
                    <DialogTitle className="text-lg">Edit Employee</DialogTitle>
                    <div className="flex gap-1 ml-auto">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={cancelEditing}
                        disabled={isSaving}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={saveChanges}
                        disabled={isSaving}
                      >
                        {isSaving ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4 mr-1" />
                        )}
                        {isSaving ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="edit-name" className="text-xs">Name</Label>
                      <Input
                        id="edit-name"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Employee name"
                        className="h-8"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="edit-email" className="text-xs">Email</Label>
                      <Input
                        id="edit-email"
                        type="email"
                        value={editEmail}
                        onChange={(e) => setEditEmail(e.target.value)}
                        placeholder="email@example.com"
                        className="h-8"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="edit-role" className="text-xs">Role</Label>
                      <Select
                        value={editRoleId || 'none'}
                        onValueChange={(value) => setEditRoleId(value === 'none' ? null : value)}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Select a role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No role assigned</SelectItem>
                          {roles?.map((role) => (
                            <SelectItem key={role.id} value={role.id}>
                              {role.name}
                              {role.default_pay_rate && (
                                <span className="text-muted-foreground ml-1">
                                  (${role.default_pay_rate.toFixed(2)}/hr)
                                </span>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="edit-employment-type" className="text-xs">Employment Type</Label>
                      <Select
                        value={editEmploymentTypeId || 'none'}
                        onValueChange={(value) => setEditEmploymentTypeId(value === 'none' ? null : value)}
                        disabled={loadingTypes}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder={loadingTypes ? "Loading..." : "Select type"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Not specified</SelectItem>
                          {employmentTypes?.map((type) => (
                            <SelectItem key={type.id} value={type.id}>
                              {type.label_native}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1 col-span-2">
                      <Label htmlFor="edit-salary-adj" className="text-xs">Salary Adjustment ($/hr)</Label>
                      <Input
                        id="edit-salary-adj"
                        type="number"
                        step="0.01"
                        value={editSalaryAdjustment || ''}
                        onChange={(e) => setEditSalaryAdjustment(e.target.value ? parseFloat(e.target.value) : null)}
                        placeholder="e.g., 2.50"
                        className="h-8 w-40"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <DialogTitle className="text-xl">{employee.name}</DialogTitle>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={startEditing}
                        className="h-7 px-2"
                      >
                        <Edit className="h-3.5 w-3.5 mr-1" />
                        Edit
                      </Button>
                    </div>
                    <DialogDescription className="flex flex-wrap items-center gap-3 mt-1">
                      {employee.email && (
                        <span className="flex items-center gap-1.5 text-sm">
                          <Mail className="h-3.5 w-3.5" />
                          {employee.email}
                        </span>
                      )}
                      {employee.phone && (
                        <span className="flex items-center gap-1.5 text-sm">
                          <Phone className="h-3.5 w-3.5" />
                          {employee.phone}
                        </span>
                      )}
                    </DialogDescription>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <Badge
                        className={`${getContractStatusConfig(employee.contract_status || 'active').bgColor} ${getContractStatusConfig(employee.contract_status || 'active').textColor}`}
                      >
                        {formatContractStatus(employee.contract_status || 'active')}
                      </Badge>
                      {employee.roleInfo?.name && (
                        <Badge variant="outline">
                          <Briefcase className="h-3 w-3 mr-1" />
                          {employee.roleInfo.name}
                        </Badge>
                      )}
                      {employee.employmentType && (
                        <Badge variant="secondary">{employee.employmentType.label_native}</Badge>
                      )}
                      {employee.salary_adjustment && (
                        <Badge variant="outline" className="text-green-600">
                          <DollarSign className="h-3 w-3 mr-0.5" />
                          +{employee.salary_adjustment.toFixed(2)}/hr
                        </Badge>
                      )}
                    </div>
                  </div>
                  {/* Plan Progress Summary */}
                  <div className="text-right min-w-[140px] flex-shrink-0">
                    <div className="text-xs text-muted-foreground mb-1">Monthly Progress</div>
                    {fulfillmentLoading ? (
                      <div className="text-sm text-muted-foreground">Loading...</div>
                    ) : fulfillment ? (
                      <div className="space-y-1">
                        <div className={cn('text-2xl font-bold', config.color)}>
                          {fulfillment.hoursWorked}
                          <span className="text-sm font-normal text-muted-foreground">
                            /{fulfillment.hoursTarget}h
                          </span>
                        </div>
                        <div className={cn('flex items-center justify-end gap-1 text-xs', config.color)}>
                          <StatusIcon className="h-3.5 w-3.5" />
                          <span>{config.label}</span>
                        </div>
                        <Progress
                          value={Math.min(fulfillment.progressPercent, 100)}
                          className="h-1.5"
                        />
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground">No target set</div>
                    )}
                  </div>
                </>
              )}
            </div>
          </DialogHeader>

          {/* Scrollable Content - min-height ensures consistent modal size across tabs */}
          <div className="flex-1 overflow-y-auto px-6 py-4 min-h-[500px]">
            {/* Quick Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <Card>
                <CardContent className="pt-3 pb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-primary/10 rounded-lg">
                      <Clock className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <div className="text-xl font-bold">
                        {fulfillment?.hoursWorked || 0}h
                      </div>
                      <div className="text-xs text-muted-foreground">Actual</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-3 pb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <CalendarDays className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                        {fulfillment?.hoursPlanned || 0}h
                      </div>
                      <div className="text-xs text-muted-foreground">Planned</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-3 pb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-green-100 dark:bg-green-900/30 rounded-lg">
                      <Calendar className="h-4 w-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <div className="text-xl font-bold">
                        {fulfillment?.shiftsCompleted || 0}/{fulfillment?.shiftsScheduled || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">Shifts</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-3 pb-3">
                  <div className="flex items-center gap-2">
                    <div className={cn("p-1.5 rounded-lg", config.bgColor)}>
                      <StatusIcon className={cn("h-4 w-4", config.color)} />
                    </div>
                    <div>
                      <div className={cn("text-xl font-bold", config.color)}>
                        {fulfillment?.actualProgressPercent || 0}%
                      </div>
                      <div className="text-xs text-muted-foreground">Actual %</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-3 pb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                      <TrendingUp className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <div className="text-xl font-bold">
                        {fulfillment?.expectedProgressPercent || 0}%
                      </div>
                      <div className="text-xs text-muted-foreground">Expected</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tabbed Content */}
            <Tabs defaultValue={defaultTab} className="space-y-4">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="time">Time</TabsTrigger>
                <TabsTrigger value="payroll">Payroll</TabsTrigger>
                <TabsTrigger value="documents">Documents</TabsTrigger>
                <TabsTrigger value="activity">Activity</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview">
                {/* Month Navigation */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium">
                    Monthly Overview
                  </h3>
                  <div className="inline-flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={handlePrevMonth}
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <MonthPicker
                      value={selectedMonth}
                      onChange={handleMonthSelect}
                      size="sm"
                      className="h-8"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={handleNextMonth}
                    >
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Two-column layout: Calendar (70%) | Plan & Stats (30%) */}
                <div className="grid grid-cols-[7fr_3fr] gap-4">
                  {/* Left Column - Calendar */}
                  <EmployeeShiftCalendarView
                    shifts={calendarShifts}
                    employee={employee}
                    businessId={businessId}
                    loading={loadingShifts}
                    selectedMonth={selectedMonth}
                    className="h-[500px] overflow-y-auto"
                  />

                  {/* Right Column - Plan Progress & Quick Stats */}
                  <div className="space-y-4">
                    {/* Plan Progress Details */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Plan Progress</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {!fulfillment || fulfillment.status === 'no_target' ? (
                          <div className="text-center py-4 text-muted-foreground text-sm">
                            No monthly target set for this role
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {/* Actual Progress */}
                            <div className="space-y-1">
                              <div className="flex justify-between text-xs">
                                <span className={config.color}>Actual (tracked)</span>
                                <span className="font-medium">
                                  {fulfillment.actualProgressPercent}% ({fulfillment.hoursWorked}/{fulfillment.hoursTarget}h)
                                </span>
                              </div>
                              <Progress value={Math.min(fulfillment.actualProgressPercent, 100)} className="h-2" />
                            </div>
                            {/* Planned Progress */}
                            <div className="space-y-1">
                              <div className="flex justify-between text-xs">
                                <span className="text-blue-600 dark:text-blue-400">Planned (shifts)</span>
                                <span className="font-medium">
                                  {fulfillment.plannedProgressPercent}% ({fulfillment.hoursPlanned}/{fulfillment.hoursTarget}h)
                                </span>
                              </div>
                              <Progress
                                value={Math.min(fulfillment.plannedProgressPercent, 100)}
                                className="h-2"
                                indicatorClassName="bg-blue-500"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs pt-2">
                              <div>
                                <div className="text-muted-foreground">Expected</div>
                                <div className="font-medium">{fulfillment.expectedProgressPercent}%</div>
                              </div>
                              <div>
                                <div className="text-muted-foreground">Status</div>
                                <div className={cn('font-medium flex items-center gap-1', config.color)}>
                                  <StatusIcon className="h-3 w-3" />
                                  {config.label}
                                </div>
                              </div>
                              <div>
                                <div className="text-muted-foreground">Completed</div>
                                <div className="font-medium">{fulfillment.shiftsCompleted} shifts</div>
                              </div>
                              <div>
                                <div className="text-muted-foreground">Scheduled</div>
                                <div className="font-medium">{fulfillment.shiftsScheduled} shifts</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Quick Stats */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Quick Stats</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 text-xs">
                          <div className="flex justify-between items-center p-2 rounded-lg bg-muted/50">
                            <span className="text-muted-foreground">This Month</span>
                            <span className="font-semibold">
                              {calendarShifts.length} shifts
                            </span>
                          </div>
                          <div className="flex justify-between items-center p-2 rounded-lg bg-muted/50">
                            <span className="text-muted-foreground">Hours Scheduled</span>
                            <span className="font-semibold">
                              {calendarShifts.reduce((sum, s) => {
                                const start = new Date(s.start_time);
                                const end = new Date(s.end_time);
                                return sum + (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                              }, 0).toFixed(0)}h
                            </span>
                          </div>
                          <div className="flex justify-between items-center p-2 rounded-lg bg-muted/50">
                            <span className="text-muted-foreground">Contract</span>
                            <span className="font-medium">
                              {employee?.contract_status
                                ? formatContractStatus(employee.contract_status)
                                : 'Not specified'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center p-2 rounded-lg bg-muted/50">
                            <span className="text-muted-foreground">Type</span>
                            <span className="font-medium">
                              {employee?.employmentType?.name || 'Not set'}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>

              {/* Time & Attendance Tab */}
              <TabsContent value="time">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Recent Clock Events</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {recentTimeLogs.length === 0 ? (
                      <div className="text-center py-6 text-muted-foreground text-sm">
                        No time logs recorded yet
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {recentTimeLogs.map((log) => (
                          <div
                            key={log.id}
                            className="flex items-center justify-between p-2 border rounded-lg text-sm"
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className={cn(
                                  'p-1.5 rounded-full',
                                  log.event_type === 'arrival'
                                    ? 'bg-green-100 dark:bg-green-900/30'
                                    : 'bg-orange-100 dark:bg-orange-900/30'
                                )}
                              >
                                {log.event_type === 'arrival' ? (
                                  <UserCheck className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                                ) : (
                                  <Timer className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
                                )}
                              </div>
                              <div>
                                <div className="font-medium">
                                  {log.event_type === 'arrival' ? 'Clock In' : 'Clock Out'}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {log.location_name || 'Main Location'}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-medium">
                                {new Date(log.scanned_at).toLocaleTimeString('en-GB', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {new Date(log.scanned_at).toLocaleDateString('en-GB')}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Payroll Tab */}
              <TabsContent value="payroll">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Compensation Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div>
                          <div className="text-xs text-muted-foreground">Hourly Rate</div>
                          <div className="text-xl font-bold flex items-center gap-1">
                            <DollarSign className="h-4 w-4" />
                            {employee.hourly_rate?.toFixed(2) || '0.00'}
                          </div>
                        </div>
                        {employee.roleInfo?.hours_per_month && (
                          <div>
                            <div className="text-xs text-muted-foreground">Monthly Target Hours</div>
                            <div className="text-base font-medium">{employee.roleInfo.hours_per_month}h</div>
                          </div>
                        )}
                        <div>
                          <div className="text-xs text-muted-foreground">Employment Type</div>
                          <div className="text-base font-medium">
                            {employee.employmentType?.label_native || 'Not specified'}
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <div className="text-xs text-muted-foreground">This Month Earnings (Est.)</div>
                          <div className="text-xl font-bold text-green-600 dark:text-green-400">
                            ${((fulfillment?.hoursWorked || 0) * (employee.hourly_rate || 0)).toFixed(2)}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Role</div>
                          <div className="text-base font-medium">
                            {employee.roleInfo?.name || 'No role assigned'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Documents Tab */}
              <TabsContent value="documents">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Employee Documents</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-6 text-muted-foreground">
                      <FileText className="h-10 w-10 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">No documents uploaded yet</p>
                      <p className="text-xs">
                        Contracts and other documents will appear here
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Activity Timeline Tab */}
              <TabsContent value="activity">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Activity Timeline</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loadingActivities ? (
                      <div className="text-center py-6 text-muted-foreground text-sm">Loading activities...</div>
                    ) : activities.length === 0 ? (
                      <div className="text-center py-6 text-muted-foreground text-sm">No activities yet</div>
                    ) : (
                      <div className="space-y-2">
                        {activities.map((activity) => (
                          <div
                            key={activity.id}
                            className="flex items-start gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                          >
                            <div className="p-1.5 bg-muted rounded-full">{getActivityIcon(activity.type)}</div>
                            <div className="flex-1 min-w-0">
                              {renderActivityContent(activity)}
                              <div className="text-xs text-muted-foreground mt-1">
                                {new Date(activity.created_at).toLocaleString('en-GB')}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Fixed Footer */}
          <DialogFooter className="flex-shrink-0 px-6 py-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EmployeeDetailModal;
